import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { FaArrowLeft } from "react-icons/fa";
import { useLocation, useNavigate, type NavigateOptions } from "react-router-dom";

interface BackButtonProps {
  fallback: string;
}

const historyIndex = (): number | null => {
  const index = window.history.state?.idx;
  return typeof index === "number" ? index : null;
};

export const hasInternalLumenHistory = (): boolean => (historyIndex() ?? 0) > 0;

// Which Lumen path each history entry of this tab holds, by history.state.idx. Per tab, so it
// survives a refresh like the browser history itself does.
const HISTORY_PATHS_KEY = "lumen.historyPaths";

const readHistoryPaths = (): Record<string, string> => {
  try {
    const stored = JSON.parse(window.sessionStorage.getItem(HISTORY_PATHS_KEY) || "{}");
    return stored && typeof stored === "object" ? stored : {};
  } catch {
    return {};
  }
};

/** Records the path of every Lumen history entry; render it once inside the router. */
export const LumenHistoryRecorder = () => {
  const location = useLocation();

  useEffect(() => {
    const index = historyIndex();
    if (index === null) return;
    const paths = readHistoryPaths();
    paths[index] = location.pathname;
    try {
      window.sessionStorage.setItem(HISTORY_PATHS_KEY, JSON.stringify(paths));
    } catch {
      // Without storage a finished form is replaced instead of left by stepping back.
    }
  }, [location]);

  return null;
};

const pathnameOf = (target: string) => target.split(/[?#]/)[0];

/**
 * Back to the Lumen page the user came from; without such a page (bookmark, refresh, shared link,
 * new tab) to the internal fallback, so the back action never leaves Lumen.
 */
export const useBackNavigation = (fallback: string) => {
  const navigate = useNavigate();

  return useCallback(() => {
    if (hasInternalLumenHistory()) {
      navigate(-1);
      return;
    }
    navigate(fallback, { replace: true });
  }, [navigate, fallback]);
};

/**
 * After a successful save or finished action on a form (edit, new, migrate): go to the target page
 * without leaving the form in the history. When the entry before the form is that target (the details
 * page the user came from), step back to it, so the details keep their own origin for Back. Otherwise
 * (opened directly, or a different target) the form's entry is replaced by the target.
 */
export const useLeaveForm = () => {
  const navigate = useNavigate();

  return useCallback((target: string, options: Omit<NavigateOptions, "replace"> = {}) => {
    const index = historyIndex();
    if (index !== null && index > 0 && readHistoryPaths()[index - 1] === pathnameOf(target)) {
      navigate(-1);
      return;
    }
    navigate(target, { ...options, replace: true });
  }, [navigate]);
};

const BackButton = ({ fallback }: BackButtonProps) => {
  const goBack = useBackNavigation(fallback);
  const { t } = useTranslation();

  return (
    <button
      type="button"
      onClick={goBack}
      className="inline-flex items-center gap-3 rounded-full px-2 py-2 text-sm font-semibold text-slate-600 transition hover:bg-slate-100 hover:text-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:text-slate-300 dark:hover:bg-white/10 dark:hover:text-white"
    >
      <FaArrowLeft aria-hidden="true" className="text-indigo-600 dark:text-indigo-300" />
      {t("common.back", { defaultValue: "Back" })}
    </button>
  );
};

export default BackButton;
